import { Alert, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { REASON_MAX } from './hrApi'
import { voidRating, type Rating } from './ratingApi'
import { useQuarterLabel } from './useQuarterLabel'

interface Props {
  rating: Rating
  onClose: () => void
}

interface FormValues {
  reason: string
}

export function RatingVoidModal({ rating, onClose }: Props) {
  const { t } = useTranslation()
  const quarterLabel = useQuarterLabel()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const submit = useMutation({
    mutationFn: (values: FormValues) => voidRating(rating.id, values.reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      onClose()
    },
  })

  const close = () => {
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open
      title={t('hr.rating.voidTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values))}
      confirmLoading={submit.isPending}
      okText={t('hr.rating.voidSubmit')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('hr.rating.voidNote', { quarter: quarterLabel(rating.quarter) })}
      />

      {submit.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(submit.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" disabled={submit.isPending}>
        <Form.Item
          name="reason"
          label={t('hr.rating.voidReason')}
          rules={[
            { required: true, whitespace: true, message: t('hr.rating.validation.reasonRequired') },
            { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
          ]}
        >
          <Input.TextArea rows={3} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  )
}
